import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Compass } from "@/lib/icons";

const HERO = "https://images.unsplash.com/photo-1511269974491-7a969994a71f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = mode === "login"
      ? await login(email, password)
      : await register({ email, password, name });
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--dl-bg)" }}>
      {/* Brand hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(7,11,20,0.55), rgba(7,11,20,0.92))" }} />
        <div className="relative flex items-center gap-3">
          <div className="grid place-items-center h-11 w-11 rounded-xl" style={{ background: "linear-gradient(135deg,#2563EB,#0EA5E9)" }}>
            <Compass size={24} className="text-white" />
          </div>
          <div className="font-display font-extrabold text-2xl text-white">DockLinc <span className="text-sky-400">C⁶</span></div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-bold text-white leading-tight">Your World.<br />Connected.</h1>
          <p className="text-slate-300 mt-4 max-w-md">Capture observations, media, people, places and stories — then catalog, connect, and continue the work over time.</p>
          <div className="flex flex-wrap gap-2 mt-6 font-mono-label text-[11px] text-slate-400">
            {["Capture", "Catalog", "Connect", "Create", "Collaborate", "Continue"].map((c) => (
              <span key={c} className="px-3 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>{c}</span>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">Small moments. Bigger connections.</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm dl-rise">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="grid place-items-center h-10 w-10 rounded-xl" style={{ background: "linear-gradient(135deg,#2563EB,#0EA5E9)" }}><Compass size={22} className="text-white" /></div>
            <div className="font-display font-extrabold text-xl text-white">DockLinc <span className="text-sky-400">C⁶</span></div>
          </div>
          <h2 className="font-display text-2xl font-bold text-white">{mode === "login" ? "Welcome back" : "Create your workspace"}</h2>
          <p className="text-sm text-slate-400 mb-6">{mode === "login" ? "Sign in to your private workspace." : "Private by default. Your world, your rules."}</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Your name</Label>
                <Input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required className="bg-black/30" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Email</Label>
              <Input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-black/30" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Password</Label>
              <Input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-black/30" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
            <Button data-testid="submit-auth-btn" type="submit" disabled={busy} className="w-full capture-glow" style={{ background: "#2563EB" }}>
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create workspace"}
            </Button>
          </form>

          <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-sm text-slate-400 mt-5 hover:text-sky-400 w-full text-center">
            {mode === "login" ? "New here? Create a workspace" : "Already have a workspace? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
